import { FieldState } from 'formstate';
import { action, computed, makeObservable, observable } from 'mobx';

export interface DateRangeValue {
    start: Date;
    end: Date;
}

export class DateRangeFieldState extends FieldState<DateRangeValue | undefined> {
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(initialValue?: DateRangeValue) {
        super(initialValue);
    }

    onChangeHandler = (range: DateRangeValue | undefined) => {
        this.onChange(range?.start && range?.end ? range : undefined);
    };
}
interface SelectOptionSimple<T> {
    text: string;
    value: T;
    data?: any;
}
interface SelectOptionExtended<T, TD> {
    text: string;
    value: T;
    data: TD;
}

export type SelectOption<T, TD = void> = [TD] extends [void]
    ? SelectOptionSimple<T>
    : SelectOptionExtended<T, TD>;

export interface SelectOptionGroup<TO> {
    text: string;
    options: TO[];
}

export function isGroupedOptions<T, TD = void>(
    opts: SelectOption<T, TD>[] | SelectOptionGroup<SelectOption<T, TD>>[]
): opts is SelectOptionGroup<SelectOption<T, TD>>[] {
    return (
        !!opts.length && Array.isArray((opts[0] as SelectOptionGroup<SelectOption<T, TD>>).options)
    );
}

export abstract class SelectFieldState<T, TV = T | T[], TD = void> extends FieldState<
    TV | undefined
> {
    private selectOptions?: SelectOption<T, TD>[];
    private selectGroupOptions?: SelectOptionGroup<SelectOption<T, TD>>[];

    abstract multiple: boolean;
    abstract onChangeHandler: (
        data: SelectOption<T, TD> | SelectOption<T, TD>[] | undefined
    ) => void;

    protected constructor(initValues?: TV) {
        super(initValues);

        makeObservable<
            FieldState<TV | undefined>,
            | 'selectOptions'
            | 'selectGroupOptions'
            | 'flatOptions'
            | 'hasOptions'
            | 'options'
            | 'setOptions'
            | 'setOptionGroups'
            | 'selectedOptions'
            | 'isSet'
        >(this, {
            selectOptions: observable,
            selectGroupOptions: observable,
            flatOptions: computed,
            hasOptions: computed,
            selectedOptions: computed,
            options: computed,
            isSet: computed,
            setOptions: action,
            setOptionGroups: action,
        });
    }

    get isSet() {
        return this.value !== undefined;
    }

    get hasOptions() {
        return !!this.selectOptions?.length || !!this.selectGroupOptions?.length;
    }

    get options(): SelectOption<T, TD>[] | SelectOptionGroup<SelectOption<T, TD>>[] {
        return this.selectGroupOptions ?? this.selectOptions ?? [];
    }

    get optionGroups(): SelectOptionGroup<SelectOption<T, TD>>[] {
        return (
            this.selectGroupOptions ??
            (this.selectOptions
                ? [
                      {
                          text: '',
                          options: this.selectOptions,
                      },
                  ]
                : [])
        );
    }

    get flatOptions(): SelectOption<T, TD>[] {
        return (
            this.selectOptions ??
            this.selectGroupOptions?.reduce(
                (out, group) => [...out, ...group.options],
                [] as SelectOption<T, TD>[]
            ) ??
            []
        );
    }

    setOptions(opts: SelectOption<T, TD>[] | undefined) {
        if (!opts) {
            this.selectGroupOptions = undefined;
            this.selectOptions = undefined;
        } else {
            this.selectGroupOptions = undefined;
            this.selectOptions = opts;
        }

        this.onChange(this.getCleanValue());
    }

    setOptionGroups(opts: SelectOptionGroup<SelectOption<T, TD>>[] | undefined) {
        if (!opts) {
            this.selectGroupOptions = undefined;
            this.selectOptions = undefined;
        } else {
            this.selectGroupOptions = opts;
            this.selectOptions = undefined;
        }

        this.onChange(this.getCleanValue());
    }

    get selectedOptions(): SelectOption<T, TD>[] {
        const values = (
            Array.isArray(this.value) ? this.value : this.value ? [this.value] : undefined
        ) as T[] | undefined;

        return !values?.length
            ? []
            : this.hasOptions
              ? this.flatOptions.filter(opt => values.includes(opt.value))
              : (values.map(val => ({ value: val, text: `${val}` })) as SelectOption<T, TD>[]);
    }

    private getCleanValue(): TV | undefined {
        if (!this.selectOptions && !this.selectGroupOptions) {
            return this.value;
        }

        const flatOptions = this.flatOptions;
        const value = this.value;

        if (Array.isArray(value)) {
            return value.filter(val => flatOptions.find(opt => opt.value === val)) as unknown as TV;
        } else if (value) {
            if (flatOptions.find(opt => opt.value === (value as any))) {
                return value;
            }
        }
    }
}

export class SingleSelectFieldState<T, TD = void> extends SelectFieldState<T, T, TD> {
    readonly multiple = false;

    constructor(initValue?: T) {
        super(initValue);

        makeObservable<FieldState<T | undefined>, 'selectedOption'>(this, {
            selectedOption: computed,
        });
    }

    static WithOptions<T, TD = never>(options: SelectOption<T, TD>[], initValues?: T) {
        const inst = new SingleSelectFieldState<T, TD>(initValues);

        inst.setOptions(options);

        return inst;
    }

    static WithOptionGroups<T, TD = never>(
        options: SelectOptionGroup<SelectOption<T, TD>>[],
        initValues?: T
    ) {
        const inst = new SingleSelectFieldState<T, TD>(initValues);

        inst.setOptionGroups(options);

        return inst;
    }

    get selectedOption(): SelectOption<T, TD> | undefined {
        const value = this.value;

        return !value
            ? undefined
            : this.hasOptions
              ? this.flatOptions.find(opt => value === opt.value)
              : ({ value, text: `${value}` } as SelectOption<T, TD>);
    }

    onChangeHandler = (data: SelectOption<T, TD> | SelectOption<T, TD>[] | undefined) => {
        if (Array.isArray(data)) {
            this.onChange(data.length ? data[0].value : undefined);
        } else if (data?.value !== undefined) {
            this.onChange(data.value);
        } else {
            this.onChange(undefined);
        }
    };
}

export class MultipleSelectFieldState<T, TD = void> extends SelectFieldState<T, T[], TD> {
    readonly multiple = true;

    // it is not useless because parent constructor is protected
    // eslint-disable-next-line @typescript-eslint/no-useless-constructor
    constructor(initValue?: T[]) {
        super(initValue);
    }

    static WithOptions<T, TD = never>(options: SelectOption<T, TD>[], initValues?: T[]) {
        const inst = new MultipleSelectFieldState<T, TD>(initValues);

        inst.setOptions(options);

        return inst;
    }

    static WithOptionGroups<T, TD = never>(
        options: SelectOptionGroup<SelectOption<T, TD>>[],
        initValues?: T[]
    ) {
        const inst = new MultipleSelectFieldState<T, TD>(initValues);

        inst.setOptionGroups(options);

        return inst;
    }

    get isSet() {
        return !!this.value?.length;
    }

    onChangeHandler = (data: SelectOption<T, TD> | SelectOption<T, TD>[] | undefined) => {
        if (Array.isArray(data)) {
            this.onChange(data.length ? data.map(d => d.value) : undefined);
        } else if (data?.value !== undefined) {
            this.onChange([data.value]);
        } else {
            this.onChange(undefined);
        }
    };
}
